
// Define industry types
export interface Industry {
  id: string;
  name: string;
  verticals?: string[];
}

// Import industries data from JSON file
import industriesData from '../config/industries.json';

// List of industries for selection in forms
export const industries: Industry[] = industriesData;

// Helper function to get industry by ID
export const getIndustryById = (id: string): Industry | undefined => {
  return industries.find(industry => industry.id === id);
};

// Helper function to get industry verticals
export const getIndustryVerticals = (industryId: string): string[] => {
  const industry = getIndustryById(industryId);
  return industry?.verticals || [];
};

// Import differentiators data to check for configured differentiators
import differentiatorsData from '@/config/differentiators.json';

// Minimal map for aliases NOT directly found as an 'id' in industries.json
// or for cases where an industryId should map to a *different* service key
// than its own ID.
const industryAliasMap: Record<string, string> = {
  "home-services": "plumbing", // Example: if "home-services" is a conceptual group
  "technology": "hardware"    // Example: if "technology" is a conceptual group
  // Add other specific aliases if necessary
};

// Helper to get the appropriate service data key for an industry
export const getServiceDataKey = (industryId: string): string | undefined => {
  const normalizedInputId = industryId.toLowerCase();

  // 1. Check direct ID match in industries.json
  // The 'id' from industries.json is assumed to be the serviceKey if it exists
  const industryFromConfig = industriesData.find(
    (ind) => ind.id.toLowerCase() === normalizedInputId
  );
  if (industryFromConfig) {
    return industryFromConfig.id; // Return the id from industries.json as the serviceKey
  }

  // 2. Check alias map for special conceptual inputs or overrides
  if (industryAliasMap[normalizedInputId]) {
    return industryAliasMap[normalizedInputId];
  }

  // 3. No match found
  console.error(
    `getServiceDataKey: Service key configuration for industry ID "${industryId}" not found. ` +
    `Ensure it exists as an 'id' in 'src/config/industries.json' or is mapped in 'industryAliasMap'.`
  );
  return undefined;
};

// Helper to determine if an industry should use differentiators
export const shouldUseIndustryDifferentiators = (industryId: string): boolean => {
  const serviceKey = getServiceDataKey(industryId);

  if (!serviceKey) {
    // If the serviceKey couldn't be determined, assume no differentiators
    // or handle as an error case depending on desired behavior.
    // For now, returning false seems safest.
    // The warning is already logged by getServiceDataKey if serviceKey is undefined.
    return false;
  }
  
  // Check if differentiators are defined for this serviceKey in differentiators.json
  // Assumes differentiatorsData.differentiators is an object like: { "plumbing": [...], "landscaping": [...] }
  if (differentiatorsData.differentiators && 
      typeof (differentiatorsData.differentiators as Record<string, unknown>)[serviceKey] !== 'undefined') {
    // The key exists, meaning differentiators are configured for this industry (even if the list is empty)
    return true;
  }
  
  // No differentiator configuration found for this serviceKey
  return false;
};
